// This file is a wrapper around the react-big-calendar component
//  that applies Axle's theme and styling to the calendar.
//  Right now, that's all it does, until we have a better idea what common
//  elements will be shared across all of our future calendar components.
import { Box } from '@mui/material';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { Calendar, CalendarProps, DayLayoutAlgorithm, momentLocalizer } from 'react-big-calendar';

import { uniqueByEquality } from '../../utils/iteration';
import { centerChildren } from '../utils/centering';
import './react-big-calendar.css';

export type AxleEvent = {
    // Normal properties for React Big Calendar.
    title: string;

    // Passing in Dates instead of moments here because of this issue:
    // https://stackoverflow.com/questions/57287782/typeerror-dateget-method-is-not-a-function-in-react-big-calendar
    start: Date;
    end: Date;

    // Custom properties
    uuid: string;
    resourceId: string;
};

// Resources must have a timezone property. This is used to localize events in the calendar correctly.
export type AxleResource = {
    uuid: string;
    timezone: string;
};

export const AxleCalendar = <T extends AxleEvent, R extends AxleResource>({
    events,
    backgroundEvents,
    resources,
    getStyleForEvent,
    date,
    // If noEventsText is provided, we'll display it when there are no events.
    //  This isn't an overlay, it is in place of the calendar.
    noEventsText = undefined,
    ...props
}: {
    events: T[];
    backgroundEvents: T[];
    resources: R[];
    getStyleForEvent?: (event: T) => object;
    noEventsText?: string;
    date?: Date;
} & Omit<
    CalendarProps<T, R>,
    'events' | 'backgroundEvents' | 'resources' | 'date' | 'localizer'
>) => {
    const dateLocalizer = momentLocalizer(moment);

    // Create an object that maps resource IDs to their timezones.
    const resourceTimezones = resources.reduce<Record<string, string>>((acc, resource) => {
        acc[resource.uuid] = resource.timezone;
        return acc;
    }, {});

    const localizeEvents = (events: T[]) => {
        return events
            .filter((event) => event.resourceId in resourceTimezones)
            .map((event) => {
                return {
                    ...event,
                    start: new Date(
                        moment(event.start)
                            .tz(resourceTimezones[event.resourceId])
                            .format('YYYY-MM-DD HH:mm'),
                    ),
                    end: new Date(
                        moment(event.end)
                            .tz(resourceTimezones[event.resourceId])
                            .format('YYYY-MM-DD HH:mm'),
                    ),
                };
            });
    };

    // This very funny looking code is solving for a very beguiling problem:
    //  react-big-calendar has a bug where overlapping events spill into the next column.
    //  If we change the `dayLayoutAlgorithm` to a different value and then back again,
    //  it seems to work fine. We have to do this every time the event data changes.
    //  The following code accomplishes this by setting `dayLayoutAlgorithm` to the dummy
    //  value of 'no-overlap' every time the event data changes, only to change it back to
    //  our desired value of 'overlap' right after.
    const [dayLayoutAlgorithm, setDayLayoutAlgorithm] = useState('overlap');
    useEffect(() => {
        setDayLayoutAlgorithm('no-overlap');
    }, [backgroundEvents, events]);
    useEffect(() => {
        if (dayLayoutAlgorithm === 'no-overlap') {
            setDayLayoutAlgorithm('overlap');
        }
    }, [dayLayoutAlgorithm]);

    // The current time indicator should be shown only if all resources are in the same timezone,
    //  and if so, the current time indicator should be adjusted to match that timezone.
    const resourceTimezonesValues = _.values(resourceTimezones);
    const areAllResourcesInSameTimezone =
        resourceTimezonesValues.length > 0 && uniqueByEquality(resourceTimezonesValues).length <= 1;
    const uniqueTimezone = resourceTimezonesValues.length > 0 ? resourceTimezonesValues[0] : null;
    const getNow = () => {
        if (uniqueTimezone) {
            return new Date(moment().tz(uniqueTimezone).format('YYYY-MM-DD HH:mm'));
        }
        return new Date(); // Fallback to current local time if no timezone is available
    };

    return events.length === 0 && backgroundEvents.length === 0 && noEventsText ? (
        <Box height={'100%'} {...centerChildren}>
            {noEventsText}
        </Box>
    ) : (
        <Box
            sx={{
                '.rbc-current-time-indicator': {
                    // This line hides the current time indicator, if resources
                    //  belong to different timezones, otherwise it does nothing.
                    display: areAllResourcesInSameTimezone ? undefined : 'none',
                },
            }}
        >
            <Calendar
                localizer={dateLocalizer}
                events={localizeEvents(events)}
                backgroundEvents={localizeEvents(backgroundEvents)}
                resources={resources}
                eventPropGetter={getStyleForEvent}
                dayLayoutAlgorithm={dayLayoutAlgorithm as DayLayoutAlgorithm}
                date={date}
                // TODO: need to fix this; see AXL-4831 for more
                onNavigate={() => undefined}
                showMultiDayTimes={true}
                getNow={getNow}
                style={{ zIndex: 1, position: 'relative' }}
                {...props}
            />
        </Box>
    );
};
